"use client"

import { useEffect, useCallback, useSyncExternalStore, useRef } from "react"
import { KEY_DETAILS_STORAGE_KEY, MEMORY_CHANGED_EVENT, type Memory } from "@/lib/constants/memory"
import { getStorageAdapter, getAdapterMode, debugAdapterSelection, type AdapterMode } from "@/lib/memory/get-adapter"
import type { IStorageAdapter } from "@/lib/memory/storage-adapter"
import { useDemoStore, getIsDemoMode } from "@/lib/stores/demo-store"

// ============================================================================
// External Store for useSyncExternalStore
// ============================================================================

interface BusinessBrainState {
  memories: Memory[]
  keyDetailsCompleted: number
  adapterMode: AdapterMode
  isLoading: boolean
  error: Error | null
}

let cachedState: BusinessBrainState = {
  memories: [],
  keyDetailsCompleted: 0,
  adapterMode: "session",
  isLoading: true,
  error: null,
}

let isInitialized = false
let currentAdapter: IStorageAdapter | null = null

let isAuthenticated = false

export function setAuthenticatedState(authenticated: boolean): void {
  if (isAuthenticated !== authenticated) {
    isAuthenticated = authenticated
    console.log("[BusinessBrain] Auth state changed:", authenticated)
    debugAdapterSelection({ isDemoMode: getIsDemoMode(), isAuthenticated: authenticated })
    refreshFromAdapter()
  }
}

const listeners = new Set<() => void>()

async function initializeFromAdapter(): Promise<void> {
  if (typeof window === "undefined") return

  const isDemoMode = getIsDemoMode()
  const adapter = getStorageAdapter({ isDemoMode, isAuthenticated })
  currentAdapter = adapter

  console.log("[BusinessBrain] Initializing with adapter:", adapter.name)

  try {
    const [memories, keyDetailsCompleted] = await Promise.all([
      adapter.getMemories(true),
      adapter.getKeyDetailsCompleted(),
    ])

    cachedState = {
      memories,
      keyDetailsCompleted,
      adapterMode: getAdapterMode({ isDemoMode, isAuthenticated }),
      isLoading: false,
      error: null,
    }
  } catch (error) {
    console.error("[BusinessBrain] Failed to initialize:", error)
    cachedState = {
      ...cachedState,
      isLoading: false,
      error: error instanceof Error ? error : new Error("Failed to initialize"),
    }
  }
}

async function refreshFromAdapter(): Promise<void> {
  if (typeof window === "undefined") return

  const isDemoMode = getIsDemoMode()
  const adapter = getStorageAdapter({ isDemoMode, isAuthenticated })

  if (currentAdapter?.name !== adapter.name) {
    console.log(`[BusinessBrain] Adapter switched: ${currentAdapter?.name} -> ${adapter.name}`)
    currentAdapter = adapter
  }

  try {
    const [memories, keyDetailsCompleted] = await Promise.all([
      adapter.getMemories(true),
      adapter.getKeyDetailsCompleted(),
    ])

    cachedState = {
      memories,
      keyDetailsCompleted,
      adapterMode: getAdapterMode({ isDemoMode, isAuthenticated }),
      isLoading: false,
      error: null,
    }
  } catch (error) {
    console.error("[BusinessBrain] Failed to refresh:", error)
    cachedState = {
      ...cachedState,
      isLoading: false,
      error: error instanceof Error ? error : new Error("Failed to refresh"),
    }
  }

  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)

  if (!isInitialized) {
    isInitialized = true
    initializeFromAdapter().then(() => {
      listeners.forEach((l) => l())
    })
  }

  const handleStorageChange = () => {
    refreshFromAdapter()
  }

  if (typeof window !== "undefined") {
    window.addEventListener(MEMORY_CHANGED_EVENT, handleStorageChange)
    window.addEventListener("storage", handleStorageChange)
  }

  return () => {
    listeners.delete(listener)
    if (typeof window !== "undefined") {
      window.removeEventListener(MEMORY_CHANGED_EVENT, handleStorageChange)
      window.removeEventListener("storage", handleStorageChange)
    }
  }
}

function getSnapshot(): BusinessBrainState {
  return cachedState
}

function getServerSnapshot(): BusinessBrainState {
  return { memories: [], keyDetailsCompleted: 0, adapterMode: "session", isLoading: true, error: null }
}

// ============================================================================
// Hook
// ============================================================================

export function useBusinessBrain() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
  const { isDemoMode } = useDemoStore()
  const hasInitialized = useRef(false)
  const prevDemoMode = useRef(isDemoMode)

  useEffect(() => {
    if (prevDemoMode.current !== isDemoMode) {
      prevDemoMode.current = isDemoMode
      refreshFromAdapter()
    }
  }, [isDemoMode])

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true
      refreshFromAdapter()
    }
  }, [])

  const getAdapter = useCallback((): IStorageAdapter => {
    return getStorageAdapter({ isDemoMode, isAuthenticated })
  }, [isDemoMode])

  const saveMemory = useCallback(
    async (memory: { field: string; label: string; value: string; section: string }): Promise<void> => {
      const adapter = getAdapter()

      if (adapter.isReadOnly) {
        console.warn("[BusinessBrain] Cannot save in read-only mode (demo)")
        return
      }

      console.log("[v0][BusinessBrain] saveMemory called:", {
        field: memory.field,
        label: memory.label,
        section: memory.section,
      })

      await adapter.saveMemory(memory)

      console.log("[v0][BusinessBrain] Memory saved to adapter, refreshing state")
      await refreshFromAdapter()

      console.log("[v0][BusinessBrain] State refreshed, new memory count:", cachedState.memories.length)
    },
    [getAdapter],
  )

  const updateMemory = useCallback(
    async (id: string, updates: Partial<Memory>): Promise<void> => {
      const adapter = getAdapter()

      if (adapter.isReadOnly) {
        console.warn("[BusinessBrain] Cannot update in read-only mode (demo)")
        return
      }

      await adapter.updateMemory(id, updates)
      await refreshFromAdapter()
    },
    [getAdapter],
  )

  const deleteMemory = useCallback(
    async (id: string): Promise<void> => {
      const adapter = getAdapter()

      if (adapter.isReadOnly) {
        console.warn("[BusinessBrain] Cannot delete in read-only mode (demo)")
        return
      }

      await adapter.deleteMemory(id)
      await refreshFromAdapter()
    },
    [getAdapter],
  )

  const archiveMemory = useCallback(
    async (id: string): Promise<void> => {
      const adapter = getAdapter()

      if (adapter.isReadOnly) {
        console.warn("[BusinessBrain] Cannot archive in read-only mode (demo)")
        return
      }

      await adapter.archiveMemory(id)
      await refreshFromAdapter()
    },
    [getAdapter],
  )

  const restoreMemory = useCallback(
    async (id: string): Promise<void> => {
      const adapter = getAdapter()

      if (adapter.isReadOnly) {
        console.warn("[BusinessBrain] Cannot restore in read-only mode (demo)")
        return
      }

      await adapter.restoreMemory(id)
      await refreshFromAdapter()
    },
    [getAdapter],
  )

  const getMemoryByField = useCallback(
    async (fieldId: string): Promise<Memory | undefined> => {
      const adapter = getAdapter()
      return adapter.getMemoryByField(fieldId)
    },
    [getAdapter],
  )

  const getMemoryCount = useCallback((): number => {
    return state.memories.filter((m) => !m.isArchived).length
  }, [state.memories])

  const getKeyDetailsCompleted = useCallback((): number => {
    return state.keyDetailsCompleted
  }, [state.keyDetailsCompleted])

  const clearAllMemories = useCallback(async (): Promise<void> => {
    const adapter = getAdapter()

    if (adapter.isReadOnly) {
      console.warn("[BusinessBrain] Cannot clear in read-only mode (demo)")
      return
    }

    await adapter.clearAll()
    await refreshFromAdapter()
  }, [getAdapter])

  const getMemoriesForContext = useCallback((): Array<{ label: string; value: string }> => {
    return state.memories.filter((m) => !m.isArchived).map((m) => ({ label: m.label, value: m.value }))
  }, [state.memories])

  const retry = useCallback(async (): Promise<void> => {
    await refreshFromAdapter()
  }, [])

  return {
    memories: state.memories.filter((m) => !m.isArchived),
    allMemories: state.memories,
    memoryCount: state.memories.filter((m) => !m.isArchived).length,
    keyDetailsCompleted: state.keyDetailsCompleted,
    adapterMode: state.adapterMode,
    isLoading: state.isLoading,
    isReadOnly: isDemoMode,
    error: state.error,

    saveMemory,
    updateMemory,
    deleteMemory,
    archiveMemory,
    restoreMemory,
    getMemoryByField,
    getMemoryCount,
    getKeyDetailsCompleted,
    clearAllMemories,
    getMemoriesForContext,
    retry,
  }
}

// ============================================================================
// Standalone Actions (for use outside React components)
// ============================================================================

export const businessBrainActions = {
  getMemories: async () => {
    const isDemoMode = getIsDemoMode()
    const adapter = getStorageAdapter({ isDemoMode, isAuthenticated })
    return adapter.getMemories()
  },
  saveMemory: async (memory: { field: string; label: string; value: string; section: string }) => {
    const isDemoMode = getIsDemoMode()
    const adapter = getStorageAdapter({ isDemoMode, isAuthenticated })

    if (adapter.isReadOnly) {
      console.warn("[BusinessBrain] Cannot save in read-only mode (demo)")
      return
    }

    await adapter.saveMemory(memory)

    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(MEMORY_CHANGED_EVENT))
    }
  },
  getKeyDetailsCompleted: async () => {
    const isDemoMode = getIsDemoMode()
    const adapter = getStorageAdapter({ isDemoMode, isAuthenticated })
    return adapter.getKeyDetailsCompleted()
  },
  incrementKeyDetails: async () => {
    if (typeof window === "undefined") return
    const isDemoMode = getIsDemoMode()
    if (isDemoMode) return

    try {
      const stored = sessionStorage.getItem(KEY_DETAILS_STORAGE_KEY)
      const current = stored ? Number.parseInt(stored, 10) : 0
      sessionStorage.setItem(KEY_DETAILS_STORAGE_KEY, Math.min(5, current + 1).toString())
      window.dispatchEvent(new CustomEvent(MEMORY_CHANGED_EVENT))
    } catch (error) {
      console.error("[BusinessBrain] Failed to increment key details:", error)
    }
  },
  setAuthenticated: setAuthenticatedState,
}
